// Global Instructions Rule Applied!

import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'
import { EmailCampaignCopySchema } from './email_campaign_copy_model.ts'

// Read shape of one Email Campaigns row.
//
// Single superset model — same approach as distribution_list_grid_model.ts.
// Grid uses the lean subset of keys; cold-load detail uses all keys. Detail-
// only keys are .optional() so a grid row passes validation without carrying
// them. .strict() rejects unknown keys, so a future schema addition fails
// loudly until the model is extended (CLAUDE.md: a response that fails
// validation is an error, not coerced).

export const EmailCampaignAttachmentSchema = z
  .object({
    id: z.string(),
    file_name: z.string(),
    file_url: z.string(),
    date_created: z.string()
  })
  .strict()

export const EmailCampaignGridModelSchema = z
  .object({
    // Always present (grid + detail)
    email_campaigns__id: z.string(),
    email_campaigns__name: z.string(),
    email_campaigns__subject: z.string(),
    email_campaigns__status: z.string(),
    email_campaigns__send_mode: z.string(),
    // Postmark message-stream selector (broadcast | transactional). Always
    // present (grid + detail); the compose form's Send-type toggle.
    email_campaigns__channel: z.string(),
    email_campaigns__email_template_id: z.string().nullable().optional(),
    email_campaigns__email_template_name: z.string(),
    email_campaigns__sent_as_user_id: z.string(),
    email_campaigns__sent_as_user_name: z.string(),
    email_campaigns__sent_as_user_email: z.string(),
    // Phase 5: created-by (operator) audit dimension — surfaces alongside
    // sent_as_user for send-on-behalf events. Detail RPC has historically
    // omitted both; grid RPC now always emits them.
    email_campaigns__created_by: z.string().nullable().optional(),
    email_campaigns__created_by_name: z.string().nullable().optional(),
    email_campaigns__recipient_count: z.number(),
    email_campaigns__sent_count: z.number(),
    email_campaigns__failed_count: z.number(),
    // Phase 4b: DISTINCT account_id count among staged recipients. Only the
    // detail RPC emits this today; grid RPC will get it in Phase 5 if useful.
    email_campaigns__team_count: z.number().nullable().optional(),
    email_campaigns__dispatched_at: z.string().nullable().optional(),
    // Phase 5: campaign completion timestamp — now always emitted by grid
    // RPC for the Sent log column. Still nullable for in-flight campaigns.
    email_campaigns__completed_at: z.string().nullable().optional(),
    email_campaigns__date_modified: z.string(),

    // Detail-only — every optional is also .nullable() because the backend
    // zon validator (add_nullables=True) materializes missing optional
    // fields as JSON null.
    email_campaigns__body_html: z.string().nullable().optional(),
    // 'jodit' (default) = sanitized + thin-shell-wrapped on dispatch;
    // 'imported' = bypassed end-to-end; 'ckeditor' (Phase 2) = sanitized
    // like 'jodit'.
    email_campaigns__body_source: z.enum(['jodit', 'imported', 'ckeditor']).nullable().optional(),
    email_campaigns__from_address: z.string().nullable().optional(),
    email_campaigns__team_greeting_mode: z.string().nullable().optional(),
    email_campaigns__email_template_key: z.string().nullable().optional(),
    email_campaigns__comms_topic_id: z.string().nullable().optional(),
    email_campaigns__resolution_params: z.unknown().nullable().optional(),
    email_campaigns__attachments: z.array(EmailCampaignAttachmentSchema).nullable().optional(),
    // Campaign-level Cc / Bcc copy recipients (detail RPC embeds these so the
    // compose form rehydrates them on a cold load / hard refresh) + the
    // advisory queue-warning threshold (cfg-driven; null when unseeded).
    email_campaigns__cc_recipients: z.array(EmailCampaignCopySchema).nullable().optional(),
    email_campaigns__bcc_recipients: z.array(EmailCampaignCopySchema).nullable().optional(),
    email_campaigns__cc_bcc_large_list_threshold: z.number().nullable().optional(),
    // Dispatch progress + halt (detail-only; 2026-09-15). The form shows
    // "Dispatch stalled" when a queued/sending campaign has made no recipient
    // progress for stall_warning_seconds (cfg email/dispatch_stall; null when
    // unseeded). seconds_since_progress is computed against the DB clock.
    email_campaigns__last_progress_at: z.string().nullable().optional(),
    email_campaigns__seconds_since_progress: z.number().nullable().optional(),
    email_campaigns__pending_count: z.number().nullable().optional(),
    // "Check Postmark & re-queue" candidates: stuck sending, or failed by the
    // stall sweep without a provider message id.
    email_campaigns__unconfirmed_count: z.number().nullable().optional(),
    email_campaigns__halted_at: z.string().nullable().optional(),
    email_campaigns__halted_by_name: z.string().nullable().optional(),
    email_campaigns__stall_warning_seconds: z.number().nullable().optional(),
    // Per-account attachments are required (detail-only): every staged
    // account must carry its own file before the queue accepts the campaign.
    email_campaigns__account_attachments_required: z.boolean().nullable().optional(),
    // Notice email (detail-only; capital call notices, stage 7): the capital
    // call this campaign was created to send the notices of. Null otherwise.
    email_campaigns__capital_call_id: z.string().nullable().optional(),
    capital_calls__name: z.string().nullable().optional(),
    // Statement email (detail-only; investor statements, A3.14 stage 6): the
    // statement run this campaign was created to send the statements of.
    email_campaigns__investor_statement_run_id: z.string().nullable().optional(),
    investor_statement_runs__name: z.string().nullable().optional(),
    // The document groups the statement email carries (reports, statements;
    // docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1) and their labels for
    // the source link ("Statement run: ... · Fund reports").
    investor_statement_email_campaigns__document_group_codes: z.array(z.string()).nullable().optional(),
    prog__statement_document_groups_label: z.string().nullable().optional(),
    // The part of its set the statement email is (d.3): "(email 2 of 3)" on
    // the source link; 1 of 1 for an email that stands alone.
    investor_statement_email_campaigns__part_no: z.number().nullable().optional(),
    investor_statement_email_campaigns__part_count: z.number().nullable().optional()
  })
  .strict()

export const EmailCampaignGridModelFields = makeFieldKeys(EmailCampaignGridModelSchema)
export type EmailCampaignGridModel = z.infer<typeof EmailCampaignGridModelSchema>
export type EmailCampaignAttachment = z.infer<typeof EmailCampaignAttachmentSchema>

// Sender option (search_system_users_for_select + view__my_eligible_email_senders).
// `is_self` is only emitted by the eligible-senders RPC (so the picker can
// surface "me" at the top). Optional so both shapes validate against one model.
export const EmailSenderOptionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
    email: z.string().nullable().optional(),
    // Backend validator (zon add_nullables=True) materializes missing
    // optional fields as JSON null, so accept null in addition to omitted.
    is_self: z.boolean().nullable().optional()
  })
  .strict()

export type EmailSenderOption = z.infer<typeof EmailSenderOptionSchema>

// Delegations list (view__email_sender_delegations)
export const EmailDelegationGrantedToMeSchema = z
  .object({
    id: z.string(),
    grantor_user_id: z.string(),
    grantor_label: z.string(),
    grantor_email: z.string().nullable().optional(),
    date_created: z.string()
  })
  .strict()

export const EmailDelegationGrantedByMeSchema = z
  .object({
    id: z.string(),
    grantee_user_id: z.string(),
    grantee_label: z.string(),
    grantee_email: z.string().nullable().optional(),
    date_created: z.string()
  })
  .strict()

export const EmailDelegationsModelSchema = z
  .object({
    granted_to_me: z.array(EmailDelegationGrantedToMeSchema),
    granted_by_me: z.array(EmailDelegationGrantedByMeSchema)
  })
  .strict()

export type EmailDelegationGrantedToMe = z.infer<typeof EmailDelegationGrantedToMeSchema>
export type EmailDelegationGrantedByMe = z.infer<typeof EmailDelegationGrantedByMeSchema>
export type EmailDelegationsModel = z.infer<typeof EmailDelegationsModelSchema>

// My signature (read response)
export const EmailMySignatureSystemUserSchema = z
  .object({
    id: z.string(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    work_title: z.string().nullable().optional(),
    mobile_phone: z.string().nullable().optional()
  })
  .strict()

export const EmailMySignatureModelSchema = z
  .object({
    signature_html: z.string(),
    firm_default_html: z.string(),
    resolved_html: z.string(),
    used_firm_default: z.boolean(),
    system_user: EmailMySignatureSystemUserSchema
  })
  .strict()

export type EmailMySignatureModel = z.infer<typeof EmailMySignatureModelSchema>

// Resolve-signature response (POST /email/signatures/resolve)
export const EmailResolveSignatureModelSchema = z
  .object({
    html: z.string(),
    used_firm_default: z.boolean(),
    sent_as_user_id: z.string()
  })
  .strict()

export type EmailResolveSignatureModel = z.infer<typeof EmailResolveSignatureModelSchema>

// Validators

export function validateEmailCampaignGridModel(data: unknown): EmailCampaignGridModel {
  try {
    return validateWithZodSchema(EmailCampaignGridModelSchema, data, 'email campaign grid model')
  } catch (error) {
    throw new Error(`Email campaign grid model validation failed: ${error}`)
  }
}

export function validateEmailSenderOption(data: unknown): EmailSenderOption {
  try {
    return validateWithZodSchema(EmailSenderOptionSchema, data, 'email sender option')
  } catch (error) {
    throw new Error(`Email sender option validation failed: ${error}`)
  }
}

export function validateEmailDelegationsModel(data: unknown): EmailDelegationsModel {
  try {
    return validateWithZodSchema(EmailDelegationsModelSchema, data, 'email delegations model')
  } catch (error) {
    throw new Error(`Email delegations model validation failed: ${error}`)
  }
}

export function validateEmailMySignatureModel(data: unknown): EmailMySignatureModel {
  try {
    return validateWithZodSchema(EmailMySignatureModelSchema, data, 'my signature model')
  } catch (error) {
    throw new Error(`My signature model validation failed: ${error}`)
  }
}

export function validateEmailResolveSignatureModel(data: unknown): EmailResolveSignatureModel {
  try {
    return validateWithZodSchema(
      EmailResolveSignatureModelSchema,
      data,
      'email resolve signature model'
    )
  } catch (error) {
    throw new Error(`Email resolve signature model validation failed: ${error}`)
  }
}
